import { ansprache, type Ansprachform, type Personenangabe, type Rolle, type Rollenbezug } from "@dj/domain/gemeinsam";
import { useNavigate, useParams } from "react-router";
import { standBezeichnung } from "@dj/domain/revier";
import { useDokument, useSammlung } from "../plattform/lesemodelle";
import { Kachel } from "../design/Kachel";
import { Status } from "../design/Hinweis";
import { EigeneRolle, PersonMitBezeichnung } from "../design/Person";
import { Symbol } from "../design/Symbol";
import { navigieren } from "../plattform/position";
import { AnsichtDerDrueckjagd } from "./AnsichtDerDrueckjagd";
import { Abschnitt, Angaben, Leer, namen, wert, useKopf, useMeineAufgaben, useRevier, useRevierSammlung } from "./teile";

/**
 * Ansichten einer Drückjagd für alle Mitwirkenden (oberflaeche.md §6): Einweisung, Meine Aufgaben, Notfallinformationen,
 * Standbesetzung, Hunde im Treiben, Teilnehmerübersicht der Drückjagd. Sie zeigen nur, was die Lesemodelle der Person
 * hergeben (Leserlisten, ADR-0010); vor der Bekanntgabe ist das nur der Kopf (F-062).
 */

export function Einweisung() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const revier = useRevier(id);
  const einweisung = useDokument(revier ? `drueckjagden/${id}/reviere/${revier}/einweisung/aktuell` : null);
  const tagesablauf = useSammlung(`drueckjagden/${id}/tagesablauf`);
  const bestimmungen = useSammlung(`drueckjagden/${id}/sicherheitsbestimmungen`);
  const hinweise = useSammlung(`drueckjagden/${id}/hinweise`);
  const kontakte = useSammlung(`drueckjagden/${id}/notfallkontakte`);
  const punkte = useSammlung(`drueckjagden/${id}/rettungspunkte`);
  const e = einweisung.daten;
  // Referenz `#s-einweisung`: Hinweise zuerst, Bereiche je Teil (Sicherheit zuerst), „Revierkarte“ und
  // „Notfallinformationen“ fest unten.
  return (
    <AnsichtDerDrueckjagd drueckjagdId={id} name="Einweisung" unten={(
      <div className="raster raster--2 liste__unten" data-gleich="">
        <Kachel symbol="karte" begriff="Revierkarte" art="kompakt" onPress={() => navigate(`/drueckjagd/${id}/revierkarte`)} />
        <Kachel symbol="rettungspunkt" begriff="Notfallinformationen" art="kompakt" fuge onPress={() => navigate(`/drueckjagd/${id}/notfallinformationen`)} />
      </div>
    )}>
      <Status>Die App ergänzt Jagdsignale und Anweisungen vor Ort, sie ersetzt sie nicht.</Status>
      {!navigator.onLine && !einweisung.daten ? <Status symbol="offline">Die Einweisung ist vor dem Jagdtag nur mit Netz lesbar.</Status> : null}
      <Abschnitt name="Sicherheitsbestimmungen" symbol="info" text>
        {bestimmungen.daten.map((b) => <p key={b._id}>{String(b["Sicherheitsbestimmung"])}</p>)}
      </Abschnitt>
      <Abschnitt name="Treffpunkt" symbol="standort" text><p>{wert(e?.["Treffpunkt"])}</p></Abschnitt>
      <Abschnitt name="Tagesablauf" symbol="uhr" text>
        {tagesablauf.daten.map((p) => (
          <p key={p._id}><span className="zeitstempel">{p["ca."] ? "ca. " : ""}{String(p["Uhrzeit"])}</span> {String(p["Programmpunkt"])}</p>
        ))}
      </Abschnitt>
      <Abschnitt name="Hinweise" symbol="info" text>
        {hinweise.daten.map((h) => <p key={h._id}>{String(h["Hinweis"])}</p>)}
      </Abschnitt>
      <Abschnitt name="Wildarten" symbol="rotwild" text><p>{wert(e?.["Wildarten"])}</p></Abschnitt>
      <Abschnitt name="Notfallkontakte" symbol="telefon" text>
        {kontakte.daten.map((k) => <p key={k._id}>{String(k["Bezeichnung"])} · {String(k["Telefonnummer"] ?? k["Anruf"])}</p>)}
      </Abschnitt>
      <Abschnitt name="Rettungspunkte" symbol="rettungspunkt" text>
        {punkte.daten.map((r) => <p key={r._id}><span className="kennung">{String(r["Kennung"])}</span> · {wert(r["Lage"])}</p>)}
      </Abschnitt>
      <Status>Nur Meldungen zählen. Ein Schuss, der nur als Nachricht geschrieben wird, gilt nicht als gemeldet.</Status>
    </AnsichtDerDrueckjagd>
  );
}

/**
 * Feste Textstellen der Ansprache (oberflaeche.md §2.3): in der Ansprachform des eigenen Reviers (K12), mit der
 * Ansprache aus Anrede und Name (ohne Anrede Vor- und Nachname).
 */
function Begruessung({ person, form, treffpunkt }: { person: Personenangabe; form: Ansprachform; treffpunkt: string | null }) {
  const a = ansprache(person.Anrede, person.Name);
  return (
    <>
      <p className="hinweis090 platte begruessung" role="note" aria-label="Begrüßung">
        {form === "Sie" ? `Guten Tag ${a}, das sind Ihre Aufgaben.` : `Hallo ${a}, das sind deine Aufgaben.`}
      </p>
      {treffpunkt ? (
        <p className="hinweis090 platte begruessung__treffpunkt" role="note" aria-label="Hinweis zum Treffpunkt">
          <Symbol name="standort" />{form === "Sie" ? `Ihr Treffpunkt: ${treffpunkt}` : `Dein Treffpunkt: ${treffpunkt}`}
        </p>
      ) : null}
    </>
  );
}

export function MeineAufgaben() {
  const { id = "" } = useParams();
  const a = useMeineAufgaben(id).daten;
  const kopf = useKopf(id).daten;
  const zonen = (a?.["Rote Zonen"] as Array<{ "rote Zone": string; von: number; bis: number }> | null) ?? [];
  const person = a?.["_person"] as Personenangabe | undefined;
  const kennungen = a?.["_kennungen"] as Record<string, string> | undefined;
  const standort = useDokument(kennungen?.["Stand"] && kennungen["Revier"]
    ? `drueckjagden/${id}/reviere/${kennungen["Revier"]}/standbesetzung/${kennungen["Stand"]}` : null).daten;
  const position = typeof standort?.["Breite"] === "number" && typeof standort?.["Länge"] === "number"
    ? `${(standort["Breite"] as number).toFixed(6)}, ${(standort["Länge"] as number).toFixed(6)}` : null;
  return (
    <AnsichtDerDrueckjagd drueckjagdId={id} name="Meine Aufgaben">
      {!a ? <Leer text="Die Aufgaben erscheinen mit der Bekanntgabe" symbol="aufgaben" /> : (
        <>
          {person ? <Begruessung person={person} form={a["_ansprachform"] === "Sie" ? "Sie" : "Du"} treffpunkt={(a["_treffpunkt"] as string | null | undefined) ?? (kopf?.["Treffpunkt"] as string | undefined) ?? null} /> : null}
          {/* Referenz `#s-aufgaben`; die region „Eigene Rolle“ enthält nur Symbol und Bezeichnung (Vertrag §6), der
              Gruppenkopf steht davor */}
          <div className="liste-bereich platte">
            <h2 className="kopf">Eigene Rolle</h2>
            <div className="zeile eigene-rolle" role="region" aria-label="Eigene Rolle">
              <EigeneRolle anrede={person?.Anrede ?? "ohne Anrede"} rollen={(a["Rollen"] as Rolle[] | undefined) ?? []} bezug={a["_bezug"] as Rollenbezug | undefined} />
            </div>
          </div>
          {a["Stand"] ? (
            <article className="liste-bereich platte" aria-label={`Stand ${String(a["Stand"])}`}>
              <h3 className="kopf gruppenkopf"><Symbol name="stand" />{standBezeichnung(String(a["Stand"]), a["Standname"] as string | null | undefined)}</h3>
              {position ? <><span className="zeile__t lesen kennung">{position}</span>{" "}</> : null}
              {zonen.map((z) => <span key={`${z["rote Zone"]}-${z.von}`} className="zeile__u">rote Zone {z.von}°–{z.bis}° {z["rote Zone"]}</span>)}
              {((a["Ansteller"] as Personenangabe[] | null | undefined) ?? []).map((p) => (
                <div key={p.Kennung} className="zeile"><PersonMitBezeichnung person={p} rollen={["Ansteller"]} /></div>
              ))}
            </article>
          ) : null}
          <Angaben zeilen={[["Anstellgruppe", wert(a["Anstellgruppe"])], ["Treibergruppe", wert(a["Treibergruppe"])],
            ["Mitglieder der Treibergruppe", namen(a["Mitglieder der Treibergruppe"])], ["Einsatzbereich", wert(a["Einsatzbereich"])],
            ["Hundeeinsatz", wert(a["Hundeeinsatz"])]]} />
        </>
      )}
    </AnsichtDerDrueckjagd>
  );
}

export function Notfallinformationen() {
  const { id = "" } = useParams();
  const kontakte = useSammlung(`drueckjagden/${id}/notfallkontakte`);
  const punkte = useSammlung(`drueckjagden/${id}/rettungspunkte`);
  const a = useMeineAufgaben(id).daten;
  const punkt = a?.["Nächster Rettungspunkt"] as { Kennung: string; Lage: string | null } | null | undefined;
  const ziel = punkte.daten.find((r) => r["Kennung"] === punkt?.Kennung);
  const position = typeof ziel?.["Breite"] === "number" && typeof ziel?.["Länge"] === "number" ? { Breite: ziel["Breite"] as number, Länge: ziel["Länge"] as number } : null;
  // Referenz `#s-notfallinfo` (K73): oben der Rettungspunkt als Schutzfläche, darunter „112“ und „Navigation“ ohne
  // Scrollen, dann die Notfallkontakte mit „Anrufen“, unten eine Zusammenfassung im Abdruck von „Notfallinformationen“
  // der Einweisung (ohne Bedienelement).
  return (
    <AnsichtDerDrueckjagd drueckjagdId={id} name="Notfallinformationen"
      oben={(
        <>
          <section className="liste__lage liste__lage--knapp platte" role="region" aria-label="Rettungspunkt">
            <b className="kennung">{punkt ? punkt.Kennung : "—"}</b>
            {punkt?.Lage ? <span>{punkt.Lage}</span> : null}
          </section>
          <div className="raster raster--anruf liste__oben liste__112">
            <Kachel symbol="telefon" begriff="112" art="normal" className="k--anruf" onPress={() => { window.location.href = "tel:112"; }} />
            <Kachel symbol="navigation" begriff="Navigation" art="kompakt" isDisabled={!position} onPress={() => navigieren(position)} />
          </div>
        </>
      )}
      unten={<p className="liste__schutz platte" role="status"><span className="liste__text">Notruf 112{punkt ? <> · Rettungspunkt <span className="kennung">{punkt.Kennung}</span></> : null}</span></p>}>
      <section className="liste-bereich" role="region" aria-label="Notfallkontakte">
        {kontakte.daten.filter((k) => k["Anruf"] !== "112").map((k) => (
          <article key={k._id} className="eintrag eintrag--aktion eintrag--kontakt platte" aria-label={String(k["Bezeichnung"])}>
            <h3 className="eintrag__t">{String(k["Bezeichnung"])}</h3>
            <span>{String(k["Telefonnummer"] ?? k["Anruf"])}{k["Adresse"] ? ` · ${String(k["Adresse"])}` : ""}</span>
            <Kachel symbol="telefon" begriff="Anrufen" art="kompakt" onPress={() => { window.location.href = `tel:${String(k["Anruf"])}`; }} />
          </article>
        ))}
      </section>
    </AnsichtDerDrueckjagd>
  );
}

export function Standbesetzung() {
  const { id = "" } = useParams();
  const revier = useRevier(id);
  const staende = useRevierSammlung(id, revier, "standbesetzung");
  return (
    <AnsichtDerDrueckjagd drueckjagdId={id} name="Standbesetzung">
      <section className="liste-bereich" role="region" aria-label="Stände der Drückjagd">
        <h2 className="kopf gruppenkopf platte"><Symbol name="stand" />Stände</h2>
        {staende.daten.map((s) => (
          <article key={s._id} className="eintrag platte" aria-label={`Stand ${String(s["Stand"])}`}>
            <h3 className="eintrag__t">{standBezeichnung(String(s["Stand"]), s["Standname"] as string | null | undefined)}</h3>
            {s["Schütze"] ? (
              <PersonMitBezeichnung person={s["Schütze"] as Personenangabe} rollen={(s["_rollen"] as Rolle[] | undefined)?.length ? s["_rollen"] as Rolle[] : ["Schütze"]}
                bezug={{ stand: String(s["Stand"]) }} />
            ) : <span>unbesetzt</span>}
            {((s["Ansteller"] as Personenangabe[] | undefined) ?? []).map((a) => (
              <PersonMitBezeichnung key={a.Kennung} person={a} rollen={["Ansteller"]} bezug={s["Anstellgruppe"] ? { anstellgruppe: String(s["Anstellgruppe"]) } : {}} />
            ))}
            {s["Anstellstatus"] ? <span className="eintrag__status">{String(s["Anstellstatus"])}</span> : null}
          </article>
        ))}
        {staende.daten.length === 0 && !staende.laedt ? <Leer text="Keine Stände sichtbar" symbol="stand" /> : null}
      </section>
    </AnsichtDerDrueckjagd>
  );
}

export function HundeImTreiben() {
  const { id = "" } = useParams();
  const hunde = useSammlung(`drueckjagden/${id}/hunde_im_treiben`);
  return (
    <AnsichtDerDrueckjagd drueckjagdId={id} name="Hunde im Treiben">
      <section className="liste-bereich" role="region" aria-label="Hundeeinsätze der Drückjagd">
        <h2 className="kopf gruppenkopf platte"><Symbol name="hund" />Hunde im Treiben</h2>
        {hunde.daten.map((h) => (
          <article key={h._id} className="eintrag platte" aria-label={String(h["Hund"])}>
            <h3 className="eintrag__t">{String(h["Hund"])}</h3>
            <span>{wert(h["Rasse"])} · {wert(h["Farbe"])} · {wert(h["Signalhalsung bzw. Warnweste"])}</span>
            {h["Führer"] ? <PersonMitBezeichnung person={h["Führer"] as Personenangabe} rollen={h["Rolle des Führers"] ? [h["Rolle des Führers"] as Rolle] : []} /> : null}
          </article>
        ))}
        {hunde.daten.length === 0 && !hunde.laedt ? <Leer text="Keine Hunde eingesetzt" symbol="hund" /> : null}
      </section>
    </AnsichtDerDrueckjagd>
  );
}

export function TeilnehmeruebersichtDerDrueckjagd() {
  const { id = "" } = useParams();
  const revier = useRevier(id);
  const liste = useRevierSammlung(id, revier, "teilnehmeruebersicht");
  const kopf = useKopf(id);
  return (
    <AnsichtDerDrueckjagd drueckjagdId={id} name="Teilnehmerübersicht" unter={kopf.daten?.["Drückjagd"] ? String(kopf.daten["Drückjagd"]) : undefined}>
      <section className="liste-bereich" role="region" aria-label="Teilnehmer der Drückjagd">
        <h2 className="kopf gruppenkopf platte"><Symbol name="teilnehmer" />Teilnehmende</h2>
        {liste.daten.map((t) => {
          const p = t["Teilnehmer"] as Personenangabe;
          return (
            <article key={t._id} className="zeile platte" aria-label={p.Name}>
              <PersonMitBezeichnung person={p} rollen={(t["Rollen"] as Rolle[] | undefined) ?? []} bezug={t["_bezug"] as Rollenbezug | undefined} />
            </article>
          );
        })}
      </section>
    </AnsichtDerDrueckjagd>
  );
}
